import { RefObject, useEffect, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs'
import { motion, AnimatePresence } from 'framer-motion'
import { FileText, PanelRight, Brain, Globe, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { tokens } from '@/locales/translations/tokens'
import { useTranslation } from 'react-i18next'
import { UIMessage } from 'ai'
import { useReasoningTools } from '@/features/workstation/hooks/useReasoningTools'
import { ReasoningResults } from './reasoning-results'
import { useSearchGroups } from '../../hooks/useSearchGroups'
import { FileToolSearchResultSources } from '../search-result-sources/file-tool-search-result-sources'
import WebToolSearchResultSources from '../search-result-sources/web-tool-search-result-sources'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { CollapsibleSectionHeader } from './source-animated-card/collapsible-section-header'
import { format } from 'date-fns'
import { useGlobalSourceManagementStore } from '@/stores/globalSourceManagementStore'

export interface WorkspaceTabsProps {
  searchGroups: ReturnType<typeof useSearchGroups>
  className?: string
  messages: UIMessage[]
  activeTab?: string
  onTabChange?: (tab: string) => void
  isWebsearchOn: boolean
  messageRefs: RefObject<Record<string, HTMLDivElement | null>>,
  navigatedToSourcesBySystem: boolean
}

export interface SearchResultGroup {
  id: string
  results: SearchResult[]
  timestamp: number
}

export function WorkspaceTabs({ searchGroups, className, messages, activeTab = 'sources', onTabChange, isWebsearchOn, messageRefs, navigatedToSourcesBySystem }: WorkspaceTabsProps) {
  const { t } = useTranslation();
  const globalSourceManager = useGlobalSourceManagementStore();
  const [isCollapsed, setIsCollapsed] = useState(false)
  const toolGroups = useReasoningTools(messages)
  const [webSearchSourcesCollapsibleOpen, setWebSearchSourcesCollapsibleOpen] = useState(false)
  const [fileSearchSourcesCollapsibleOpen, setFileSearchSourcesCollapsibleOpen] = useState(false)

  const handleScrollToMessage = (id: string) => {
    const element = messageRefs.current[id]
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  useEffect(() => {
    const hasWebResults = searchGroups.webSearchResults.length > 0;
    const hasFileResults = searchGroups.fileSearchResults.length > 0;

    if (hasWebResults && isWebsearchOn) {
      if (navigatedToSourcesBySystem) {
        setWebSearchSourcesCollapsibleOpen(true);
      }
    } 

    if(hasFileResults && navigatedToSourcesBySystem && !isWebsearchOn) {
      setFileSearchSourcesCollapsibleOpen(true);
    }
  }, [navigatedToSourcesBySystem, searchGroups, isWebsearchOn]);


  useEffect(() => {
    if(globalSourceManager.hasActiveSource()) {
      setIsCollapsed(false);
    }
  }, [globalSourceManager]);

  return (
    <div
      className={cn('relative h-full', isCollapsed && 'cursor-pointer')}
      onClick={() => isCollapsed && setIsCollapsed(false)}
    >
      <button
        onClick={(e) => {
          e.stopPropagation()
          setIsCollapsed(!isCollapsed)
        }}
        className={cn(
          'absolute z-10 cursor-pointer rounded-md transition-all duration-300',
          isCollapsed
            ? 'left-1/2 top-3 -translate-x-1/2 transform'
            : 'right-2 top-3'
        )}
      >
        <PanelRight
          className={cn(
            'h-5 w-5 text-muted-foreground',
            isCollapsed && 'rotate-180'
          )}
        />
      </button>
      <Card
        className={cn(
          'flex h-[calc(100vh-12rem)] flex-col overflow-hidden transition-all duration-300',
          isCollapsed ? 'w-10' : 'w-[425px]',
          className
        )}
      >
        <Tabs 
          defaultValue='sources' 
          value={activeTab}
          onValueChange={onTabChange}
          className='flex h-full flex-col'
        >
          <div className='pt-10'>
            <TabsList
              className={cn(
                'mx-auto flex w-[90%] justify-between gap-2 border-b border-border px-6',
                isCollapsed && 'hidden'
              )}
            >
              {[
                { value: 'sources', icon: FileText, label: t(tokens.workstation.sources.title)},
                { value: 'reasoning', icon: Brain, label: t(tokens.workstation.reasoning.title) },
              ].map(({ value, icon: Icon, label }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className='relative flex items-center gap-2 border-b-2 border-transparent px-4 pb-2 pt-2 text-sm text-muted-foreground transition-colors hover:text-primary data-[state=active]:border-primary data-[state=active]:text-primary'
                >
                  <Icon className='h-4 w-4' />
                  <span>{label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className='mt-4 flex-1 overflow-hidden'>
            <TabsContent
              value='sources'
              className={cn(
                'mt-0 h-full overflow-y-auto',
                isCollapsed && 'hidden'
              )}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
                transition={{ delay: 0.02, duration: 0.2 }}
                className='space-y-4 h-full overflow-y-auto'
              >
                <AnimatePresence mode='popLayout' initial={false}>
                  {searchGroups.fileSearchResults.length > 0 && (
                    <Collapsible 
                      className={`relative rounded-sm border border-secondary w-[97%] mx-auto max-h-[calc(95%-3rem)] overflow-y-auto ${fileSearchSourcesCollapsibleOpen && "pb-4"}`}
                      open={fileSearchSourcesCollapsibleOpen || globalSourceManager.hasActiveSource()} 
                      onOpenChange={setFileSearchSourcesCollapsibleOpen}
                    >
                      <CollapsibleSectionHeader title={t(tokens.workstation.message.file_search_results)} icon={<FileText size={18} className="text-muted-foreground" />} />
                      <Separator className='hidden group-data-[state=open]:block mx-auto bg-muted mt-1'/>
                      <CollapsibleContent className="space-y-4 px-4 pt-2">
                        {searchGroups.fileSearchResults.map((group, index) => (
                          <motion.div
                            key={`${group.id}-${index}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0, overflow: "hidden" }}
                            transition={{ delay: index * 0.02, duration: 0.2 }}
                            className="rounded-xl shadow-sm"
                          >
                            {index > 0 && <Separator className='mt-2' />}
                            <div className="group mb-2 flex flex-col items-start justify-between hover:cursor-pointer">
                            <div className="w-full flex justify-between items-center gap-2 min-w-0">
                              <div className="flex justify-start mt-2 w-full overflow-hidden">
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {format(new Date(group.timestamp), 'HH:mm:ss')}
                                </span>
                                <span className="text-xs text-muted-foreground truncate">
                                  , {group.messageContent}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleScrollToMessage(group.id);
                              }}
                              className="flex gap-x-1 text-xs text-primary underline hover:opacity-80 transition whitespace-nowrap"
                            >
                              {t(tokens.workstation.sources.show_message)}
                              <MessageCircle color="lightgrey" strokeWidth={1.5} size={14} />
                            </button>
                          </div>
                            <div className='rounded-md px-2 py-2'>
                              <FileToolSearchResultSources results={group.results} messageId={group.id}/>
                            </div>
                          </motion.div>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                  {searchGroups.webSearchResults.length > 0 && (
                    <Collapsible 
                      className='relative rounded-sm border border-secondary w-[97%] mx-auto bg-secondary/30 max-h-[calc(100%-3rem)] overflow-y-auto'
                      open={webSearchSourcesCollapsibleOpen} 
                      onOpenChange={setWebSearchSourcesCollapsibleOpen}
                    >
                      <CollapsibleSectionHeader title={t(tokens.workstation.message.web_search_results)} icon={<Globe size={18} className="text-muted-foreground" />} />
                      <Separator className='hidden group-data-[state=open]:block mx-auto bg-muted mt-1'/>
                      <CollapsibleContent className="space-y-4 px-4 pt-2">
                        {searchGroups.webSearchResults.map((group, index) => (
                          <motion.div
                            key={`${group.id}-${index}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0, overflow: "hidden" }}
                            transition={{ delay: index * 0.02, duration: 0.2 }}
                            className="rounded-xl shadow-sm"
                          >
                            {index > 0 && <Separator className='mt-2' />}
                            <div className='rounded-md px-2 py-2'>
                              <WebToolSearchResultSources results={group.results} />
                            </div>
                          </motion.div>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                  {(searchGroups.fileSearchResults.length === 0 && searchGroups.webSearchResults.length === 0) && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className='p-8 text-center'
                    >
                      <p className='text-sm text-muted-foreground'>
                        {t(tokens.workstation.sources.empty)}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </TabsContent>
            <TabsContent
              value='reasoning'
              className={cn(
                'mt-0 h-full overflow-y-auto',
                isCollapsed && 'hidden'
              )}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
                transition={{ delay: 0.02, duration: 0.2 }}
              >
                <ReasoningResults toolGroups={toolGroups} />
              </motion.div>
            </TabsContent>
          </div>
        </Tabs>
      </Card>
    </div>
  );
}